/**
 * The contact directory and a representative's exchanges. What the widget deliberately does not have.
 *
 * The widget is scoped to one exchange because the CRM is the directory, and duplicating it there would give a
 * representative a second place to pick the wrong person. This surface has no CRM around it - that is the
 * whole reason it exists, and section 18 calls it the thing that makes the whitelabel claim concrete for a
 * customer with nothing to embed into - so the directory is here.
 *
 * Contacts and exchanges in one screen rather than two, because the question a representative actually has is
 * "what is open with this person". A separate exchange list would be a second navigation step to answer it.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  associateGenericUploadsResponseSchema,
  contactListSchema,
  contactSchema,
  createContactRequestSchema,
  genericLinkUploadsResponseSchema,
  uploadLinkResponseSchema,
  exchangeListSchema,
  exchangeSchema,
  type Contact,
  type Exchange,
  type GenericLinkUploadItem,
} from '@iqz-ddds/contracts';
import { ApiClient, Avatar, Button, Card, Field, FailureNotice, StatusPill, Tabs, type TabItem } from '@iqz-ddds/components';

type DirectoryTab = 'all';

const DIRECTORY_TABS: readonly TabItem<DirectoryTab>[] = [
  { id: 'all', label: 'All contacts' },
];

export interface DirectoryScreenProps {
  readonly client: ApiClient;
  readonly onOpen: (exchangeId: string) => void;
  readonly onFailure: (err: unknown) => { detail: string; reference?: string };
}

interface Draft {
  readonly displayName: string;
  readonly email: string;
}

const EMPTY_DRAFT: Draft = { displayName: '', email: '' };

function GenericSubmissionList({
  submissions,
  onDownload,
  busyDocumentId,
  selected,
  onToggle,
}: {
  readonly submissions: readonly GenericLinkUploadItem[] | undefined;
  readonly onDownload: (documentId: string, documentName: string) => void;
  readonly busyDocumentId: string | undefined;
  readonly selected: ReadonlySet<string>;
  readonly onToggle: (id: string) => void;
}): React.JSX.Element {
  if (submissions === undefined) return <p className="ddds-empty">Loading.</p>;
  if (submissions.length === 0) return <p className="ddds-empty">Nobody has come in through the generic upload link yet.</p>;

  return (
    <div className="ddds-table-scroll">
      <table className="ddds-table">
        <thead>
          <tr>
            <th scope="col">
              <span className="ddds-visually-hidden">Select</span>
            </th>
            <th scope="col">Name</th>
            <th scope="col">Document</th>
            <th scope="col">Date</th>
            <th scope="col">
              <span className="ddds-visually-hidden">Download</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {submissions.map((sub) => (
            <tr key={sub.id}>
              <td>
                <input
                  type="checkbox"
                  checked={selected.has(sub.id)}
                  onChange={() => onToggle(sub.id)}
                  aria-label={`Select ${sub.documentName}`}
                />
              </td>
              <td>
                <span className="ddds-table__who">
                  <Avatar name={sub.guestName} />
                  <span>
                    <span className="ddds-table__name">{sub.guestName}</span>
                    <span className="ddds-table__email">{sub.guestEmail}</span>
                  </span>
                </span>
              </td>
              <td>{sub.documentName}</td>
              <td className="ddds-table__num">{new Date(sub.createdAt).toLocaleDateString()}</td>
              <td>
                <Button
                  variant="quiet"
                  size="small"
                  disabled={busyDocumentId !== undefined}
                  onClick={() => onDownload(sub.documentId, sub.documentName)}
                  label={`Download ${sub.documentName}`}
                >
                  {busyDocumentId === sub.documentId ? 'Downloading' : 'Download'}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The directory itself: loading, empty, or the people.
 *
 * Split out so the three states read as three early returns. `contacts` is undefined until the first
 * response, which is a different thing from a representative who genuinely has nobody yet - the first says
 * "wait" and the second says "start here", and running them together in one chain of conditionals is how
 * they end up looking like the same state.
 */
function ContactList({
  contacts,
  exchanges,
  pending,
  onOpenFor,
  emptyLabel,
}: {
  readonly contacts: readonly Contact[] | undefined;
  readonly exchanges: readonly Exchange[];
  readonly pending: boolean;
  readonly onOpenFor: (contact: Contact) => Promise<void>;
  readonly emptyLabel: string;
}): React.JSX.Element {
  if (contacts === undefined) return <p className="ddds-empty">Loading.</p>;
  if (contacts.length === 0) return <p className="ddds-empty">{emptyLabel}</p>;

  /*
   * A real table, and a real one deliberately (DG.18).
   *
   * A directory is tabular data - a name, a kind, a count - and a screen reader should get the column
   * heading with each cell, which is exactly what the list of `Row`s this replaced could not give it.
   * The scroll container is on the outside so a narrow phone scrolls the table rather than the page.
   */
  return (
    <div className="ddds-table-scroll">
      <table className="ddds-table">
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">Kind</th>
            <th scope="col">Documents</th>
            <th scope="col">
              <span className="ddds-visually-hidden">Open</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {contacts.map((contact) => {
            const open = exchanges.filter((e) => e.contactId === contact.id && e.state === 'active').length;
            return (
              <tr key={contact.id}>
                <td>
                  <span className="ddds-table__who">
                    <Avatar name={contact.displayName} />
                    <span>
                      <span className="ddds-table__name">{contact.displayName}</span>
                      <span className="ddds-table__email">{contact.email}</span>
                    </span>
                  </span>
                </td>
                <td>
                  {contact.kind === 'client' ? (
                    <StatusPill tone="done">Client</StatusPill>
                  ) : (
                    <StatusPill tone="quiet">Prospect</StatusPill>
                  )}
                </td>
                <td className="ddds-table__num">{open > 0 ? 'Exchange open' : 'Not started'}</td>
                <td>
                  <Button
                    variant="quiet"
                    size="small"
                    disabled={pending}
                    onClick={() => void onOpenFor(contact)}
                    label={`Open documents for ${contact.displayName}`}
                  >
                    {open > 0 ? 'Open' : 'Start'}
                  </Button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function DirectoryScreen({ client, onOpen, onFailure }: DirectoryScreenProps): React.JSX.Element {
  const [contacts, setContacts] = useState<readonly Contact[] | undefined>(undefined);
  const [submissions, setSubmissions] = useState<readonly GenericLinkUploadItem[] | undefined>(undefined);
  const [exchanges, setExchanges] = useState<readonly Exchange[]>([]);
  const [failure, setFailure] = useState<{ detail: string; reference?: string } | undefined>(undefined);
  const [pending, setPending] = useState(false);
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT);
  const [adding, setAdding] = useState(false);
  const [uploadLink, setUploadLink] = useState<Awaited<ReturnType<typeof uploadLinkResponseSchema.parse>>['link']>(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [busyDocumentId, setBusyDocumentId] = useState<string | undefined>(undefined);
  const [selectedUploads, setSelectedUploads] = useState<ReadonlySet<string>>(new Set());
  const [targetContactId, setTargetContactId] = useState('');
  const [associating, setAssociating] = useState(false);
  const [tab, setTab] = useState<DirectoryTab>('all');

  /** Each section is its own query, so the server decides membership rather than a client-side filter of one fetch. */
  const load = useCallback(
    async (_activeTab: DirectoryTab) => {
      try {
        const [directory, uploadsRes, mine, genericLink] = await Promise.all([
          client.get('/api/v1/contacts?limit=100&source=null', contactListSchema),
          client.get('/api/v1/upload-links/me/uploads', genericLinkUploadsResponseSchema),
          // Scoped by the row-level security policy rather than by an argument, which is the point: "my
          // exchanges" and "all exchanges" are the same request and the connection decides.
          client.get('/api/v1/exchanges?limit=100', exchangeListSchema),
          client.get('/api/v1/upload-links/me', uploadLinkResponseSchema),
        ]);
        setContacts(directory.contacts);
        setSubmissions(uploadsRes.uploads);
        // Drop selections for rows that are no longer listed, e.g. after they were associated.
        setSelectedUploads((current) => new Set(uploadsRes.uploads.map((u) => u.id).filter((id) => current.has(id))));
        setExchanges(mine.exchanges);
        setUploadLink(genericLink.link);
        setFailure(undefined);
      } catch (err) {
        setFailure(onFailure(err));
      }
    },
    [client, onFailure],
  );

  const downloadDocument = useCallback(
    (documentId: string, documentName: string) => {
      setBusyDocumentId(documentId);
      void (async () => {
        try {
          const content = await client.download(`/api/v1/documents/${documentId}/content`);
          const href = content.kind === 'url' ? content.url : URL.createObjectURL(content.blob);
          const anchor = document.createElement('a');
          anchor.href = href;
          anchor.download = documentName;
          anchor.click();
          if (content.kind === 'blob') URL.revokeObjectURL(href);
        } catch (err) {
          setFailure(onFailure(err));
        } finally {
          setBusyDocumentId(undefined);
        }
      })();
    },
    [client, onFailure],
  );

  const toggleUpload = useCallback((id: string) => {
    setSelectedUploads((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const associateUploads = useCallback(async () => {
    if (selectedUploads.size === 0 || targetContactId === '') return;
    setAssociating(true);
    setFailure(undefined);
    try {
      await client.post(
        '/api/v1/upload-links/me/uploads/associate',
        { uploadIds: [...selectedUploads], contactId: targetContactId },
        associateGenericUploadsResponseSchema,
        { idempotencyKey: `associate:${globalThis.crypto.randomUUID()}` },
      );
      setSelectedUploads(new Set());
      setTargetContactId('');
      await load(tab);
    } catch (err) {
      setFailure(onFailure(err));
    } finally {
      setAssociating(false);
    }
  }, [client, load, onFailure, selectedUploads, tab, targetContactId]);

  const createUploadLink = useCallback(async () => {
    setLinkBusy(true);
    setFailure(undefined);
    try {
      const result = await client.post('/api/v1/upload-links', {}, uploadLinkResponseSchema, {
        // A new lifecycle needs a new key. Reusing a fixed key would replay the
        // first create response for 24 hours after the link was revoked.
        idempotencyKey: `upload-link:create:${globalThis.crypto.randomUUID()}`,
      });
      setUploadLink(result.link);
      setLinkCopied(false);
    } catch (err) {
      setFailure(onFailure(err));
    } finally {
      setLinkBusy(false);
    }
  }, [client, onFailure]);

  const revokeUploadLink = useCallback(async () => {
    if (uploadLink === null) return;
    setLinkBusy(true);
    setFailure(undefined);
    try {
      await client.postVoid(`/api/v1/upload-links/${uploadLink.id}/revoke`, {});
      setUploadLink(null);
      setLinkCopied(false);
    } catch (err) {
      setFailure(onFailure(err));
    } finally {
      setLinkBusy(false);
    }
  }, [client, onFailure, uploadLink]);

  const copyUploadLink = useCallback(async () => {
    if (uploadLink === null) return;
    try {
      await globalThis.navigator.clipboard.writeText(uploadLink.url);
      setLinkCopied(true);
    } catch (err) {
      setFailure(onFailure(err));
    }
  }, [onFailure, uploadLink]);

  useEffect(() => {
    setContacts(undefined);
    void load(tab);
  }, [tab, load]);

  const addContact = useCallback(async () => {
    const body = { kind: 'prospect' as const, displayName: draft.displayName.trim(), email: draft.email.trim() };
    // Parsed here as well as by the endpoint, so an obviously wrong address is refused without a round trip.
    // The endpoint remains the control; this is only about not making somebody wait to be told.
    if (!createContactRequestSchema.safeParse(body).success) {
      setFailure({ detail: 'A name and an email address are both required.' });
      return;
    }

    setPending(true);
    try {
      await client.post('/api/v1/contacts', body, contactSchema, {
        // Section 15's interceptor. A representative who double-submits the form gets one contact, and the
        // second response is the first one replayed rather than a duplicate-address conflict.
        idempotencyKey: `contact:${body.email}:${body.displayName}`,
      });
      setDraft(EMPTY_DRAFT);
      setAdding(false);
      // Adding is only offered on the `all` tab, so this always refetches that one.
      await load(tab);
    } catch (err) {
      setFailure(onFailure(err));
    } finally {
      setPending(false);
    }
  }, [client, draft, load, onFailure, tab]);

  /**
   * Open the exchange for a contact, creating one if there is none.
   *
   * The same resolve-or-create the embed endpoint performs, and for the same reason: a representative who has
   * just added somebody wants to ask them for a document, not to create a container first. The difference is
   * that here it is an explicit click rather than a side effect of minting a credential.
   */
  const openFor = useCallback(
    async (contact: Contact) => {
      const existing = exchanges.find((exchange) => exchange.contactId === contact.id && exchange.state === 'active');
      if (existing !== undefined) {
        onOpen(existing.id);
        return;
      }

      setPending(true);
      try {
        const created = await client.post(
          '/api/v1/exchanges',
          { contactId: contact.id, title: `Documents for ${contact.displayName}` },
          exchangeSchema,
          { idempotencyKey: `exchange:${contact.id}` },
        );
        onOpen(created.id);
      } catch (err) {
        setFailure(onFailure(err));
      } finally {
        setPending(false);
      }
    },
    [client, exchanges, onOpen, onFailure],
  );

  return (
    <>
      {failure !== undefined && <FailureNotice detail={failure.detail} reference={failure.reference} />}

      <Card title="Generic upload link">
        {uploadLink === null ? (
          <>
            <p className="ddds-row__meta">Create one reusable link to share with people who need to send you documents.</p>
            <Button onClick={() => void createUploadLink()} disabled={linkBusy}>
              {linkBusy ? 'Creating' : 'Create upload link'}
            </Button>
          </>
        ) : (
          <>
            <Field id="ddds-generic-upload-link" label="Your shareable link" value={uploadLink.url} onChange={() => undefined} disabled />
            <div className="ddds-rep-add">
              <Button onClick={() => void copyUploadLink()} disabled={linkBusy}>
                {linkCopied ? 'Copied' : 'Copy link'}
              </Button>
              <Button variant="quiet" onClick={() => void revokeUploadLink()} disabled={linkBusy}>
                {linkBusy ? 'Revoking' : 'Revoke link'}
              </Button>
            </div>
            <p className="ddds-row__meta">Anyone with this link can start a secure upload after verifying their contact.</p>
          </>
        )}
      </Card>

      <Card title="Via generic upload link">
        <GenericSubmissionList
          submissions={submissions}
          onDownload={downloadDocument}
          busyDocumentId={busyDocumentId}
          selected={selectedUploads}
          onToggle={toggleUpload}
        />
        {submissions !== undefined && submissions.length > 0 && (
          <div className="ddds-rep-add">
            <label className="ddds-field" htmlFor="ddds-associate-contact">
              <span className="ddds-field__label">Add selected documents to contact</span>
              <select
                id="ddds-associate-contact"
                className="ddds-field__input"
                value={targetContactId}
                disabled={associating}
                onChange={(event) => setTargetContactId(event.target.value)}
              >
                <option value="">Choose a contact</option>
                {(contacts ?? []).map((contact) => (
                  <option key={contact.id} value={contact.id}>
                    {contact.displayName} ({contact.email})
                  </option>
                ))}
              </select>
            </label>
            <Button
              onClick={() => void associateUploads()}
              disabled={associating || selectedUploads.size === 0 || targetContactId === ''}
            >
              {associating ? 'Adding' : `Add ${selectedUploads.size === 0 ? '' : `${selectedUploads.size} `}to contact`}
            </Button>
          </div>
        )}
      </Card>

      <Card title="Contacts">
        <Tabs items={DIRECTORY_TABS} selected={tab} onSelect={setTab} label="Contacts">
          <ContactList
            contacts={contacts}
            exchanges={exchanges}
            pending={pending}
            onOpenFor={openFor}
            emptyLabel="Nobody yet. Add the first person below."
          />
        </Tabs>

        {/* Every contact here already came from the link, so there is nothing to add by hand. */}
        {tab === 'all' && (adding ? (
          <form
            className="ddds-rep-add"
            onSubmit={(event) => {
              event.preventDefault();
              void addContact();
            }}
          >
            <Field
              id="ddds-contact-name"
              label="Name"
              value={draft.displayName}
              maxLength={200}
              disabled={pending}
              onChange={(displayName) => setDraft((current) => ({ ...current, displayName }))}
            />
            <Field
              id="ddds-contact-email"
              label="Email address"
              hint="Check it: everything this person is asked for, sent or reminded about goes to this address."
              value={draft.email}
              maxLength={254}
              autoComplete="off"
              disabled={pending}
              onChange={(email) => setDraft((current) => ({ ...current, email }))}
            />
            <Button type="submit" disabled={pending}>
              {pending ? 'Adding' : 'Add contact'}
            </Button>
          </form>
        ) : (
          <div className="ddds-rep-add">
            <Button variant="quiet" onClick={() => setAdding(true)}>
              Add a contact
            </Button>
          </div>
        ))}
      </Card>
    </>
  );
}
